import React from 'react'
import styled from 'styled-components'
import { breakpointFrom, breakpointUpTo } from 'utils/breakpoints'
import { TileLinkWrapper } from './TileLinkWrapper'
import { useMobileStore } from 'hooks/useMobileStore'
import { IImage } from '../../../graphql/types'
import { GatsbyImage } from 'gatsby-plugin-image'

const StyledImg = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
`

const StyledTileWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.tileBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space[1]};
  height: 100%;

  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    gap: ${({ theme }) => theme.space[2]};
  }

  ${({ theme }) => breakpointFrom(theme.breakpoints.xl)} {
    gap: ${({ theme }) => theme.space[4]};
  }
`

const StyledLinkWrapper = styled(TileLinkWrapper)`
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border: ${({ theme }) => `4px solid ${theme.colors.black}`};
    opacity: 0;
    transition: opacity 0.2s;
  }
  :hover::after {
    opacity: 1;
  }
`

const StyledHeadingContainer = styled.div`
  margin: ${({ theme }) => theme.space[4]} 0 0 0;
  height: 72px;
  max-width: 80%;
  display: flex;
  align-items: center;

  ${({ theme }) => breakpointUpTo(theme.breakpoints.md)} {
    height: 48px;
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.sm)} {
    margin: ${({ theme }) => theme.space[2]} 0 0 0;
  }
`

const StyledTileHeading = styled.h3`
  ${({ theme }) => theme.typography.SmH4}

  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    ${({ theme }) => theme.typography.MedH3}
  }

  ${({ theme }) => breakpointFrom(theme.breakpoints.lg)} {
    ${({ theme }) => theme.typography.LrgH3}
  }

  ${({ theme }) => breakpointFrom(theme.breakpoints.xl)} {
    ${({ theme }) => theme.typography.XLH3}
  }
`

const StyledParagraph = styled.p`
  margin: 0;
  padding: 0 ${({ theme }) => theme.space[4]};
  ${({ theme }) => theme.typography.XLBody1};

  ${({ theme }) => breakpointUpTo(theme.breakpoints.lg)} {
    ${({ theme }) => theme.typography.LrgBody1}
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.md)} {
    ${({ theme }) => theme.typography.MedBody1}
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.sm)} {
    display: none;
  }
`

const StyledImgWrapper = styled.figure`
  margin: 0;
  font-size: 0;
  height: 102px;
  width: 100%;

  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    height: 118px;
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.lg)} {
    height: 156px;
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.xl)} {
    height: 176px;
  }
`

export type CategoryTileProps = {
  heading: string
  img: IImage
  pathname?: string
  description?: string
}

export const CategoryTile = ({
  heading,
  img,
  pathname,
  description,
}: CategoryTileProps) => {
  const { mobileStoreLink } = useMobileStore()

  return (
    <StyledLinkWrapper
      linkTo={pathname ? `/${pathname}` : mobileStoreLink}
      isLinkExternal={!pathname}
    >
      <StyledTileWrapper>
        <StyledHeadingContainer>
          <StyledTileHeading>{heading}</StyledTileHeading>
        </StyledHeadingContainer>
        {description ? <StyledParagraph>{description}</StyledParagraph> : null}
        <StyledImgWrapper>
          <StyledImg
            image={img.image.gatsbyImageData}
            alt={img.alternativeText}
            objectFit="contain"
            objectPosition="bottom"
          />
        </StyledImgWrapper>
      </StyledTileWrapper>
    </StyledLinkWrapper>
  )
}
